import { callAgoGet, callAgoGetWithRetry } from './auth/portalSession';
import { formatApiCatchError, getApiErrorMessage, isPaginatedApiSuccess } from './apiResponseError';

export const AGO_CONTACT_MESSAGE = 'Email developer@agorisk.com if this persists.';

const DEFAULT_PARALLELISM = 4;

async function mapPool(items, concurrency, mapper) {
    const results = new Array(items.length);
    let nextIndex = 0;

    async function worker() {
        while (nextIndex < items.length) {
            const i = nextIndex;
            nextIndex += 1;
            results[i] = await mapper(items[i], i);
        }
    }

    const workers = [];
    const poolSize = Math.min(concurrency, items.length);
    for (let w = 0; w < poolSize; w += 1) {
        workers.push(worker());
    }
    await Promise.all(workers);
    return results;
}

export async function fetchPaginatedAgoList(
    baseParams,
    { maxPages = 100, parallelism = DEFAULT_PARALLELISM, onProgress } = {},
) {
    const pageLimit = maxPages;

    let res;
    try {
        res = await callAgoGetWithRetry({
            ...baseParams,
            request_type: 'GET',
        });
    } catch (e) {
        return {
            ok: false,
            error: `Error loading data — ${formatApiCatchError(e)}. ${AGO_CONTACT_MESSAGE}`,
            items: [],
        };
    }

    const initialError = getApiErrorMessage(res);
    if (initialError) {
        return { ok: false, error: `${initialError} ${AGO_CONTACT_MESSAGE}`, items: [] };
    }
    if (!isPaginatedApiSuccess(res)) {
        return {
            ok: false,
            error: `Unexpected API response. ${AGO_CONTACT_MESSAGE}`,
            items: [],
        };
    }

    let items = [...res.paginated_data];
    let cacheKey = res.paginate_cache_key;
    const totalPages = Number(res.paginate_total_pages) || 1;
    const totalItems = Number(res.paginate_total_items) || items.length;
    const pageSize = Number(res.paginate_size) || items.length;

    if (typeof onProgress === 'function') {
        onProgress(items, { done: totalPages <= 1, totalItems, totalPages, pagesLoaded: 1 });
    }

    if (totalPages > 1 && totalPages <= pageLimit) {
        const remainingPages = [];
        for (let page = 2; page <= totalPages; page += 1) {
            remainingPages.push(page);
        }

        const pageResults = await mapPool(
            remainingPages,
            Math.max(1, parallelism),
            async (page) => {
                try {
                    // Follow-up pages use the pagination cache; avoid doubling load with retries.
                    return await callAgoGet({
                        ...baseParams,
                        request_type: 'GET',
                        paginate_page: String(page),
                        paginate_size: String(pageSize),
                        paginate_cache_key: cacheKey,
                    });
                } catch (e) {
                    return {
                        __fetchError: `Error loading data — ${formatApiCatchError(e)}. ${AGO_CONTACT_MESSAGE}`,
                    };
                }
            },
        );

        for (let i = 0; i < pageResults.length; i += 1) {
            const pageRes = pageResults[i];
            if (pageRes?.__fetchError) {
                return { ok: false, error: pageRes.__fetchError, items: [] };
            }
            const pageError = getApiErrorMessage(pageRes);
            if (pageError) {
                return { ok: false, error: `${pageError} ${AGO_CONTACT_MESSAGE}`, items: [] };
            }
            if (!Array.isArray(pageRes.paginated_data)) {
                return {
                    ok: false,
                    error: `Unexpected paginated response. ${AGO_CONTACT_MESSAGE}`,
                    items: [],
                };
            }
            // Guard against backend re-slicing with a different page size.
            const returnedSize = Number(pageRes.paginate_size);
            if (returnedSize && pageSize && returnedSize !== pageSize) {
                return {
                    ok: false,
                    error: `Pagination size mismatch (${returnedSize} vs ${pageSize}). ${AGO_CONTACT_MESSAGE}`,
                    items: [],
                };
            }
            if (pageRes.paginate_cache_key) {
                cacheKey = pageRes.paginate_cache_key;
            }
            items = items.concat(pageRes.paginated_data);
            if (typeof onProgress === 'function') {
                onProgress(items, {
                    done: false,
                    totalItems,
                    totalPages,
                    pagesLoaded: i + 2,
                });
            }
        }
    } else if (totalPages > pageLimit) {
        return {
            ok: false,
            error: `List is too large to load in one step (${totalItems} items, ${totalPages} pages). ${AGO_CONTACT_MESSAGE}`,
            items: [],
        };
    }

    if (totalItems > 0 && items.length < totalItems) {
        return {
            ok: false,
            error: `Incomplete list load (${items.length} of ${totalItems}). ${AGO_CONTACT_MESSAGE}`,
            items: [],
        };
    }

    if (typeof onProgress === 'function') {
        onProgress(items, { done: true, totalItems: items.length, totalPages, pagesLoaded: totalPages });
    }

    return { ok: true, error: '', items, totalItems: items.length };
}
